<template>
  <div class="container mt-4">
    <div class="card p-4 shadow border-0" style="border-radius: 16px;">
      <h4 class="mb-4" style="color: #b03a45;">
        <i class="fas fa-wallet me-2"></i> {{ $t('budgetCategory.title') }}
      </h4>

      <form @submit.prevent="handleSubmit">
        <div class="row">
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t('budgetCategory.categoryCode') }}</label>
              <input type="text" class="form-control" v-model="form.code" required />
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t('budgetCategory.categoryName') }}</label>
              <input type="text" class="form-control" v-model="form.name" required />
            </div>
          </div>
        </div>

        <div class="d-flex gap-3 mt-3">
          <button type="submit" class="btn text-white px-4" style="background-color: #b03a45;">
            {{ editingId ? $t('budgetCategory.update') : $t('budgetCategory.save') }}
          </button>
          <button type="button" class="btn btn-secondary px-4" @click="resetForm">{{ $t('budgetCategory.cancel') }}</button>
        </div>
      </form>

      <!-- List -->
      <hr class="my-4" />
      <h5 class="mb-3" style="color: #b03a45;">
        <i class="fas fa-list me-2"></i> {{ $t('budgetCategory.existingBudgetCategories') }}
      </h5>

      <div v-if="categories.length" class="table-responsive">
        <table class="table table-bordered align-middle">
          <thead class="table-light">
            <tr>
              <th>{{ $t('budgetCategory.code') }}</th>
              <th>{{ $t('budgetCategory.name') }}</th>
              <th class="text-center">{{ $t('budgetCategory.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="category in categories" :key="category.id">
              <td>{{ category.code }}</td>
              <td>{{ category.name }}</td>
              <td class="text-center">
                <button class="btn btn-outline-danger btn-sm me-2" @click="loadCategory(category)">{{ $t('budgetCategory.edit') }}</button>
                <button class="btn btn-outline-secondary btn-sm" @click="deleteCategory(category.id)">{{ $t('budgetCategory.delete') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="text-muted">{{ $t('budgetCategory.noBudgetCategories') }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import budgetCategoryService from '@/services/budgetCategoryService'

const toast = useToast()
const categories = ref([])
const editingId = ref(null)

const form = ref({
  code: '',
  name: ''
})

const loadCategories = async () => {
  try {
    const response = await budgetCategoryService.getAll()
    categories.value = response.data
  } catch (err) {
    toast.error($t('budgetCategory.failedToLoadCategories'))
    console.error(err)
  }
}

const handleSubmit = async () => {
  try {
    if (editingId.value) {
      await budgetCategoryService.update(editingId.value, form.value)
      toast.success($t('budgetCategory.categoryUpdated'))
    } else {
      await budgetCategoryService.create(form.value)
      toast.success($t('budgetCategory.categoryCreated'))
    }
    resetForm()
    loadCategories()
  } catch (err) {
    toast.error($t('budgetCategory.failedToSaveCategory'))
    console.error(err)
  }
}

const loadCategory = (category) => {
  editingId.value = category.id
  form.value = {
    code: category.code,
    name: category.name
  }
}

const resetForm = () => {
  editingId.value = null
  form.value = {
    code: '',
    name: ''
  }
}

const deleteCategory = async (id) => {
  if (!confirm($t('budgetCategory.confirmDelete'))) return

  try {
    await budgetCategoryService.delete(id)
    categories.value = categories.value.filter(cat => cat.id !== id)
    toast.success($t('budgetCategory.categoryDeleted'))
  } catch (err) {
    toast.error($t('budgetCategory.failedToDeleteCategory'))
    console.error(err)
  }
}

onMounted(() => {
  loadCategories()
})
</script>
